import { createContext, type ReactNode, use } from 'react';
import {
  type StyleProp,
  type TextStyle,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import getOffsetColor from '@/utils/getOffsetColor';

type BadgeContextValue = {
  textColor: string;
};

interface BadgeProps extends Omit<ViewProps, 'style'> {
  children: ReactNode;
  color?: string | null;
  minWidth?: ViewStyle['minWidth'];
  maxWidth?: ViewStyle['maxWidth'];
  borderWidth?: ViewStyle['borderWidth'];
  flexShrink?: ViewStyle['flexShrink'];
  style?: StyleProp<ViewStyle>;
}

type BadgeTextProps = {
  children: ReactNode;
  numberOfLines?: number;
  style?: StyleProp<TextStyle>;
};

const BadgeContext = createContext<BadgeContextValue | null>(null);

function BadgeRoot({
  children,
  color,
  minWidth,
  maxWidth = 128,
  borderWidth = 2,
  flexShrink,
  style: extraStyles,
  ...props
}: BadgeProps) {
  const textColor = color ? getOffsetColor(color, false) : '';

  styles.useVariants({
    dynamic: !!color,
  });

  return (
    <BadgeContext
      value={{
        textColor,
      }}
    >
      <View
        {...props}
        style={[
          styles.container({
            backgroundColor: color || undefined,
            borderColor: textColor || undefined,
            borderWidth,
            flexShrink,
            maxWidth,
            minWidth,
          }),
          extraStyles,
        ]}
      >
        {children}
      </View>
    </BadgeContext>
  );
}

function BadgeText({
  children,
  numberOfLines = 1,
  style: extraStyles,
}: BadgeTextProps) {
  const context = use(BadgeContext);

  return (
    <Text
      color={context?.textColor}
      numberOfLines={numberOfLines}
      size="sm"
      style={[styles.text, extraStyles]}
    >
      {children}
    </Text>
  );
}

type BadgeStyleOptions = {
  backgroundColor?: ViewStyle['backgroundColor'];
  borderColor?: ViewStyle['borderColor'];
  borderWidth?: ViewStyle['borderWidth'];
  flexShrink?: ViewStyle['flexShrink'];
  maxWidth?: ViewStyle['maxWidth'];
  minWidth?: ViewStyle['minWidth'];
};

const styles = StyleSheet.create((theme) => ({
  container: ({
    backgroundColor,
    borderColor,
    borderWidth,
    flexShrink,
    maxWidth,
    minWidth,
  }: BadgeStyleOptions) => ({
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: backgroundColor ?? theme.colors.surface,
    borderColor: borderColor ?? theme.colors.borderMuted,
    borderRadius: theme.borderRadius.md,
    borderWidth,
    flexDirection: 'row',
    flexShrink,
    justifyContent: 'center',
    maxWidth,
    minWidth,
    variants: {
      dynamic: {
        true: {},
        false: {},
      },
    },
    minHeight: theme.size(6),
    paddingHorizontal: theme.size(2),
    paddingVertical: theme.size(0.5),
  }),
  text: {
    textTransform: 'uppercase',
  },
}));

const Badge = Object.assign(BadgeRoot, {
  Text: BadgeText,
});

export default Badge;
